import { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import hitpayApi from '_api/hitpay'
import useHitpayRedirectUrl from './useHitpayRedirectUrl'

const useFetchHitpayQr = ({
  isPaymentViaHitPay,
  isPayTogether,
  listCheckedOrder,
  orderRef,
  final,
}) => {
  const [hitpayUrl, setHitpayUrl] = useState('')
  const [isHitpayLoading, setIsHitpayLoading] = useState(
    Boolean(isPaymentViaHitPay && !isPayTogether && final > 0)
  )
  const [isHitpayFailed, setIsHitpayFailed] = useState(false)

  const paymentRequestIdRef = useRef(null)
  const lastHitpayRef = useRef({ url: '', refs: [] })

  const location = useLocation()
  const initialPath = useRef(location.pathname)
  const { addToast } = useToasts()

  const { getRedirectUrl } = useHitpayRedirectUrl()

  const getHitpayRedirectParams = () => {
    let status = null
    let reference = null

    if (location?.search) {
      const searchParams = new URLSearchParams(location.search)
      status = searchParams.get('status')
      reference = searchParams.get('reference')
    }

    if ((!status || !reference) && (location?.hash || window.location.hash)) {
      const hashStr = location?.hash || window.location.hash
      const hashClean = hashStr.replace(/^#&?/, '')
      const hashParams = new URLSearchParams(hashClean)
      if (!status) status = hashParams.get('status')
      if (!reference) reference = hashParams.get('reference')
    }

    return { status, reference }
  }

  const { status: hitpayStatus, reference: hitpayReference } = getHitpayRedirectParams()
  const isHitpayCompleted = hitpayStatus === 'completed'

  const deleteHitpayTx = () => {
    const { status: redirectStatus } = getHitpayRedirectParams()
    if (redirectStatus === 'completed') {
      return
    }

    if (paymentRequestIdRef.current) {
      if (lastHitpayRef.current.refs.length > 1) {
        hitpayApi
          .deleteHitpayTransaction({ paymentRequestId: paymentRequestIdRef.current })
          .catch(console.error)
      } else {
        hitpayApi
          .refreshHitpayPayment({
            paymentRequestId: paymentRequestIdRef.current,
            redirectUrl: getRedirectUrl(),
          })
          .catch(console.error)
      }
      paymentRequestIdRef.current = null
    }
  }

  const fetchHitpayQr = async () => {
    if (isHitpayCompleted) return null

    const payloadRefs =
      listCheckedOrder && listCheckedOrder.length > 0 ? listCheckedOrder : [orderRef]

    const isSameRefs =
      lastHitpayRef.current.refs.length === payloadRefs.length &&
      lastHitpayRef.current.refs.every((listOrderRef) => payloadRefs.includes(listOrderRef))

    if (lastHitpayRef.current.url && isSameRefs) {
      setHitpayUrl(lastHitpayRef.current.url)
      setIsHitpayFailed(false)
      return lastHitpayRef.current.url
    }

    setIsHitpayLoading(true)
    setIsHitpayFailed(false)

    if (paymentRequestIdRef.current) {
      if (lastHitpayRef.current.refs.length > 1) {
        try {
          await hitpayApi.deleteHitpayTransaction({
            paymentRequestId: paymentRequestIdRef.current,
          })
        } catch (error) {
          console.error('Failed to delete old hitpay tx', error)
        }
      } else {
        try {
          await hitpayApi.refreshHitpayPayment({
            paymentRequestId: paymentRequestIdRef.current,
            redirectUrl: getRedirectUrl(),
          })
        } catch (error) {
          console.error('Failed to refresh hitpay tx', error)
        }
      }
      paymentRequestIdRef.current = null
    }

    try {
      const { msgResp } = await hitpayApi.createHitpayPayment({
        refs: payloadRefs,
        amount: Number(final.toFixed(2)),
        redirectUrl: getRedirectUrl(),
      })
      const link = msgResp?.paymentLink || msgResp?.qrCode
      if (link) {
        setHitpayUrl(link)
        setIsHitpayFailed(false)
        if (msgResp.paymentRequestId) {
          paymentRequestIdRef.current = msgResp.paymentRequestId
          lastHitpayRef.current = { url: link, refs: payloadRefs }
        }
        return link
      }
      setIsHitpayFailed(true)
      return null
    } catch (error) {
      setIsHitpayFailed(true)
      addToast(error?.msgResp || error?.message || 'Failed to create HitPay QR', {
        appearance: 'error',
        autoDismiss: true,
      })
      return null
    } finally {
      setIsHitpayLoading(false)
    }
  }

  useEffect(() => {
    if (isPaymentViaHitPay && hitpayUrl) {
      setHitpayUrl('')
    }
    setIsHitpayFailed(false)
  }, [listCheckedOrder])

  // Clean up transaction on unmount
  useEffect(() => () => deleteHitpayTx(), [])

  // Clean up transaction on URL change (navigation)
  useEffect(() => {
    if (location.pathname !== initialPath.current) {
      deleteHitpayTx()
      initialPath.current = location.pathname
    }
  }, [location.pathname])

  // Auto-fetch HitPay QR for single order
  useEffect(() => {
    let isMounted = true

    if (isPaymentViaHitPay && !isPayTogether && !isHitpayCompleted) {
      if (final <= 0) return

      const autoFetchHitpayQr = async () => {
        setIsHitpayLoading(true)
        setIsHitpayFailed(false)

        if (paymentRequestIdRef.current) {
          if (lastHitpayRef.current.refs.length > 1) {
            try {
              await hitpayApi.deleteHitpayTransaction({
                paymentRequestId: paymentRequestIdRef.current,
              })
            } catch (error) {
              console.error('Failed to delete old hitpay tx', error)
            }
          } else {
            try {
              await hitpayApi.refreshHitpayPayment({
                paymentRequestId: paymentRequestIdRef.current,
                redirectUrl: getRedirectUrl(),
              })
            } catch (error) {
              console.error('Failed to refresh hitpay tx', error)
            }
          }
          paymentRequestIdRef.current = null
        }

        try {
          const payloadRefs = [orderRef]
          const { msgResp } = await hitpayApi.createHitpayPayment({
            refs: payloadRefs,
            amount: Number(final.toFixed(2)),
            redirectUrl: getRedirectUrl(),
          })
          const link = msgResp?.paymentLink || msgResp?.qrCode
          if (isMounted) {
            if (link) {
              setHitpayUrl(link)
              setIsHitpayFailed(false)
              if (msgResp.paymentRequestId) {
                paymentRequestIdRef.current = msgResp.paymentRequestId
                lastHitpayRef.current = { url: link, refs: payloadRefs }
              }
            } else {
              setIsHitpayFailed(true)
            }
          }
        } catch (error) {
          if (isMounted) {
            setIsHitpayFailed(true)
            addToast(error?.msgResp || error?.message || 'Failed to create HitPay QR', {
              appearance: 'error',
              autoDismiss: true,
            })
          }
        } finally {
          if (isMounted) setIsHitpayLoading(false)
        }
      }
      autoFetchHitpayQr()
    }

    return () => {
      isMounted = false
    }
  }, [isPaymentViaHitPay, isPayTogether, orderRef, final])

  return {
    hitpayUrl,
    isHitpayLoading,
    isHitpayCompleted,
    isHitpayFailed,
    hitpayReference,
    fetchHitpayQr,
  }
}

export default useFetchHitpayQr
